import { memo, MouseEvent as ReactMouseEvent } from "react"

import { ChangedPlanRecord, PlanRecordType } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { css, cx } from "@emotion/css"
import {
  formatFixedNumberCurrency,
  formatFloatingPointNumber,
} from "utils/number-format"
import { isSemanticReadonly, usePlanCachedData } from "../utils"

import { CellError } from "./CellError"
import { CellProps } from "components/TreeState"
import { FeeOption } from "api/plans"
import { FieldsKey } from "../components/AutoCompute/types"
import { Tooltip } from "@material-ui/core"
import { get } from "lodash"
import { tableAggregations } from "../components/AutoCompute/useAggregations"
import { useAtomValue } from "jotai/utils"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { useCellDialog } from "../useCellDialog"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { DEFAULT_PRECISION, usePrecision } from "providers/A12n"
import { roundHalfEven } from "../components/AutoCompute/utils"

export const PrecisePassthroughCell = memo(function _PrecisePassthroughCell(
  props: CellProps,
) {
  const precision = usePrecision()
  return <PassthroughCell {...props} precision={precision} />
})

export const PassthroughCell = memo(function _PassthroughCell(
  props: CellProps,
) {
  const {
    rowId,
    dataKey,
    columnId,
    primaryKey,
    semantic,
    format,
    disabled,
    precision = DEFAULT_PRECISION,
  } = props
  const { rowId: id } = props
  const tableSource = useTableSource()
  const aggregations = useAtomValue(tableAggregations)
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const { onChange } = useCellDialog()
  const type = readFrom[rowId]?.type as PlanRecordType
  const packaged = readFrom[rowId]?.packaged
  const isMediaSubmedia =
    type === "media_type_plan" || type === "media_subtype_plan"

  const isBooking = primaryKey === "buyingMediaFields"
  const planData = usePlanCachedData(isBooking)
  const error = get(readError, `${rowId}.${columnId}`)
  const monetary = format === "MONETARY"

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const isDisabled =
    disabled ||
    isMediaSubmedia ||
    packaged ||
    isSemanticReadonly(type, primaryKey as FieldsKey, semantic, packaged) ||
    buyingStatus === "POSTED_FOR_BILLING"

  // apply billing type rules
  const viewable = useBillingTypesRules(
    readFrom[rowId] as ChangedPlanRecord,
    primaryKey,
    semantic,
  )
  if (!viewable) return <span />

  const onOpenPassthroughCosts = (
    evt: ReactMouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const anchor = evt.currentTarget
    onChange({
      type: "passthrough",
      fieldInfo: { rowId, dataKey, primaryKey, semantic },
      anchor,
    })
  }

  const { fees, value } = (get(readFrom[rowId], `${dataKey}`) || []) as {
    fees: Array<FeeOption>
    value: number
  }
  const aggregatedValue = get(aggregations, [rowId, columnId])
  const hasAggregation = !!Object.keys(aggregations).includes(rowId)
  let appliedValue =
    isMediaSubmedia && hasAggregation && !isBooking && !packaged
      ? (aggregatedValue ?? value)
      : value

  const forcedNumeric = !isNaN(Number(appliedValue))
  appliedValue = forcedNumeric
    ? roundHalfEven(Number(appliedValue))
    : appliedValue

  const fvalue = monetary
    ? formatFloatingPointNumber(precision, 2)(appliedValue)
    : String(appliedValue)
  const labels = fees?.map((s) => s.label).join(", ")

  return (
    <div
      data-testid="passthrough-cell"
      className={styles.wrapper}
      onPointerDown={(e) => !isDisabled && onOpenPassthroughCosts(e)}
    >
      <div className={styles.info}>
        {!!fees?.length ? (
          <Tooltip title={labels || ""}>
            <div className={cx("BodyBody-xsDark-greyRegular", "labels")}>
              {labels}
            </div>
          </Tooltip>
        ) : null}
        <div className="value">
          {!!fvalue
            ? formatFixedNumberCurrency(
                fvalue,
                planData?.currencyCode,
                precision,
              )
            : ""}
        </div>
      </div>
      {error ? <CellError error={error} /> : null}
    </div>
  )
})

const styles = {
  wrapper: css`
    width: 100%;
    height: 100%;
    text-align: end;
    display: flex;
    align-items: center;
  `,
  info: css`
    width: 100%;
    font-size: 13px;
    font-weight: inherit;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 2px 0;
    .title {
      min-width: 64px;
    }
    .value {
      height: 100%;
    }
    .labels {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      padding-right: var(--spacing-xxs);
    }
    input {
      width: 100%;
      height: 100%;
      position: absolute;
      border-color: transparent;
      background-color: transparent;
      color: transparent;
      &:hover,
      &:focus,
      &::selection,
      &:active {
        border-color: transparent;
        background-color: transparent;
        color: transparent;
        outline: none;
      }
    }
  `,
}
